import React, { useState } from 'react';
import { LogOut, X, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import LanguageToggle from './LanguageToggle';
import { API_URL } from '../utils/api';
import { visibleNavItems } from '../navItems';

/**
 * The desktop sidebar's content, as a phone drawer.
 *
 * Unlike the bottom bar's More sheet, the drawer keeps the Branch Setting
 * group collapsible - same as the desktop sidebar - because there is room
 * for a disclosure here. It also carries `currentBranch` straight through to
 * `LanguageToggle`: that control reads `currentBranch?.branch_id` and saves
 * nothing at all without it, so dropping this prop would leave the language
 * select on screen but silently non-functional.
 */
const NavDrawer = ({ open, user, currentBranch, currentPage, onNavigate, onLogout, onClose, isAdmin }) => {
  const { print_lang } = useLanguage();
  const [openGroups, setOpenGroups] = useState({});

  if (!open) return null;

  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const choose = (id) => {
    onNavigate(id);
    onClose();
  };

  const items = visibleNavItems(isAdmin);

  return (
    <>
      <div className="nav-drawer-scrim" onClick={onClose} />
      <aside className="nav-drawer" role="dialog" aria-label={print_lang('menu', 'Menu')}>
        <div className="nav-drawer-header">
          <span className="nav-drawer-title">Dental Clinic</span>
          <button
            type="button"
            className="nav-drawer-close"
            onClick={onClose}
            aria-label={print_lang('close', 'Close')}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {currentBranch && (
          <div className="nav-drawer-branch">
            {currentBranch.logo_path ? (
              <img
                src={currentBranch.logo_path.startsWith('http') ? currentBranch.logo_path : `${API_URL}/${currentBranch.logo_path}`}
                alt={currentBranch.name}
                className="nav-drawer-branch-logo"
              />
            ) : (
              <Building2 size={18} aria-hidden="true" />
            )}
            <span className="truncate">{currentBranch.name}</span>
          </div>
        )}

        <nav>
          <ul className="nav-drawer-list">
            {items.map((item) => {
              if (item.children) {
                if (item.children.length === 0) return null;

                const holdsActive = item.children.some((child) => child.id === currentPage);
                // Expanded when the user opened it, and always while one of
                // its pages is the active one - otherwise landing on a child
                // page (a reload, say) shows a collapsed group with nothing
                // marked.
                const expanded = openGroups[item.id] ?? holdsActive;

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`nav-drawer-item ${holdsActive && !expanded ? 'active' : ''}`}
                      onClick={() => toggleGroup(item.id)}
                      aria-expanded={expanded}
                    >
                      <item.Icon size={20} className="flex-shrink-0" aria-hidden="true" />
                      {print_lang(item.labelKey, item.labelFallback)}
                      <span className="nav-drawer-chevron">
                        {expanded
                          ? <ChevronDown size={16} aria-hidden="true" />
                          : <ChevronRight size={16} aria-hidden="true" />}
                      </span>
                    </button>

                    {expanded && (
                      <ul className="nav-drawer-sublist">
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <button
                              type="button"
                              className={`nav-drawer-subitem ${currentPage === child.id ? 'active' : ''}`}
                              onClick={() => choose(child.id)}
                            >
                              <child.Icon size={18} className="flex-shrink-0" aria-hidden="true" />
                              {print_lang(child.labelKey, child.labelFallback)}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`nav-drawer-item ${currentPage === item.id ? 'active' : ''}`}
                    onClick={() => choose(item.id)}
                  >
                    <item.Icon size={20} className="flex-shrink-0" aria-hidden="true" />
                    {print_lang(item.labelKey, item.labelFallback)}
                  </button>
                </li>
              );
            })}
            <li>
              <button
                type="button"
                className="nav-drawer-item nav-drawer-item-danger"
                onClick={() => { onLogout(); onClose(); }}
              >
                <LogOut size={20} className="flex-shrink-0" aria-hidden="true" />
                {print_lang('logout', 'Logout')}
              </button>
            </li>
          </ul>
        </nav>

        <div className="nav-drawer-footer">
          <div className="nav-drawer-user">{user?.name}</div>
          <LanguageToggle currentBranch={currentBranch} />
        </div>
      </aside>
    </>
  );
};

export default NavDrawer;
